'use client';

import { useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { IconChevronDown } from '@/components/icons/IconChevronDown';
import type { VehicleModelGroup } from './vehicleFacetOptions';
import { VehicleFilterCheck } from './VehicleFilterCheck';

type VehicleModelFilterProps = {
    groups: VehicleModelGroup[];
    selected: string[];
    onToggle: (key: string) => void;
    onClear: () => void;
};

function triggerLabel(groups: VehicleModelGroup[], selected: string[]): string {
    if (!selected.length) return 'Alle Modelle';
    if (selected.length > 1) return `${selected.length} Modelle`;

    const option = groups.flatMap((group) => group.options).find(({ value }) => value === selected[0]);
    return option?.label ?? '1 Modell';
}

/**
 * Model series as a dropdown of grouped checkboxes. The list opens in the flow,
 * not as a popover: the filter card clips anything positioned over its edge.
 */
export function VehicleModelFilter({ groups, selected, onToggle, onClear }: VehicleModelFilterProps) {
    const [open, setOpen] = useState(false);
    const hostRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelId = useId();

    // A press anywhere outside trigger and list closes it.
    useEffect(() => {
        if (!open) return;

        const closeOutside = (event: PointerEvent) => {
            if (!hostRef.current?.contains(event.target as Node)) setOpen(false);
        };

        document.addEventListener('pointerdown', closeOutside);
        return () => document.removeEventListener('pointerdown', closeOutside);
    }, [open]);

    if (!groups.length) return null;

    // Escape belongs to the list while it is open, not to a surrounding drawer.
    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !open) return;

        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
    };

    // Only a real focus target outside closes; a press on a row has none yet.
    const onBlur = (event: FocusEvent) => {
        const next = event.relatedTarget as Node | null;
        if (next && !hostRef.current?.contains(next)) setOpen(false);
    };

    return (
        <fieldset className="vehicle-filter__group">
            <legend className="vehicle-filter__legend label-xs">Modellreihe</legend>

            <div ref={hostRef} className="vehicle-model-filter" onKeyDown={onKeyDown} onBlur={onBlur}>
                <button
                    ref={triggerRef}
                    type="button"
                    className="vehicle-model-filter__trigger label-sm"
                    aria-expanded={open}
                    aria-controls={panelId}
                    onClick={() => setOpen((value) => !value)}
                >
                    {triggerLabel(groups, selected)}
                    <IconChevronDown className="vehicle-model-filter__chevron" aria-hidden="true" />
                </button>

                {open ? (
                    <div id={panelId} className="vehicle-model-filter__panel">
                        <VehicleFilterCheck
                            label="Alle Modelle"
                            checked={!selected.length}
                            onChange={onClear}
                            className="vehicle-model-filter__all"
                        />

                        {groups.map((group, index) => (
                            <div key={group.make} role="group" aria-labelledby={`${panelId}-make-${index}`}>
                                <div id={`${panelId}-make-${index}`} className="vehicle-model-filter__make label-xs">
                                    {group.make}
                                </div>

                                {group.options.map((option) => (
                                    <VehicleFilterCheck
                                        key={option.value}
                                        label={option.label}
                                        count={option.count}
                                        checked={selected.includes(option.value)}
                                        onChange={() => onToggle(option.value)}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </fieldset>
    );
}
